import type React from 'react';
import { array, object, type AnySchema } from 'yup';
import { BaseReportElement } from '../BaseReportElement';
import { combineRenderableContent, renderDefinitionListWithAddElementSlot } from '../renderHelpers';
import type {
    BuildStructureSettingsYupSchemaHelpers,
    ContextForKind,
    ElementDefinition,
    ElementContentRenderResult,
    ElementSettings,
    RenderContextKind,
    RenderRuntime,
} from '../types';

interface ColumnsSettings extends ElementSettings {
    columns: ElementDefinition[][];
}

export class ColumnsElement extends BaseReportElement<RenderContextKind, ColumnsSettings> {
    static readonly elementType = 'columns';

    static readonly layout = {
        flow: 'derived',
        getChildDefinitionLists: (settings: ElementSettings) =>
            (settings as ColumnsSettings).columns,
    } as const;

    static readonly compatibleContextKinds = ['project', 'measuring-point'] as const;

    static get insertion() {
        return {
            label: gettext('ELEMENT_COLUMNS_LABEL'),
            supportedSlots: ['child-list'] as const,
            createDefinition: () => ({
                type: 'columns',
                settings: { columns: [[], []] },
            }),
        };
    }

    static buildStructureSettingsYupSchema({
        buildRequiredDefinitionsSchema,
    }: BuildStructureSettingsYupSchemaHelpers): AnySchema {
        return object({
            columns: array().required().of(buildRequiredDefinitionsSchema()),
        })
            .required()
            .noUnknown();
    }

    protected toCompatibleReactNode(
        ctx: ContextForKind<RenderContextKind>,
        runtime: RenderRuntime
    ): ElementContentRenderResult {
        const childReadyPromises: Promise<void>[] = [];
        const childContentResults: Promise<boolean>[] = [];

        const columnNodes = this.settings.columns.map((columnDefinitions, columnIndex) => {
            const columnPath = runtime.getCurrentElementChildListItemPath('columns', columnIndex);
            const rendered = renderDefinitionListWithAddElementSlot(
                columnDefinitions,
                ctx,
                runtime,
                columnPath,
                ctx.kind,
                'child-list'
            );
            childReadyPromises.push(rendered.ready);
            if (columnDefinitions.length > 0) {
                childContentResults.push(rendered.hasRenderableContent);
            }

            return (
                <div key={`column-${columnIndex}`} className="flex flex-col gap-2">
                    {rendered.nodes}
                </div>
            );
        });

        const columnCount = Math.max(this.settings.columns.length, 1);

        return {
            node: (
                <div
                    className="mb-4 grid gap-4"
                    style={{
                        gridTemplateColumns: `repeat(${columnCount}, minmax(0, 1fr))`,
                    }}
                >
                    {columnNodes}
                </div>
            ),
            ready: Promise.all(childReadyPromises).then(() => undefined),
            hasRenderableContent: combineRenderableContent(childContentResults),
        };
    }
}
